import { XIcon } from "lucide-react"
import { Button } from "./button"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import type { ComponentProps } from "react"

import { cn } from "~/lib/utils"

function Sheet({ ...props }: ComponentProps<typeof SheetPrimitive.Root>) {
	return <SheetPrimitive.Root data-slot="sheet" {...props} />
}

function SheetTrigger({
	...props
}: ComponentProps<typeof SheetPrimitive.Trigger>) {
	return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />
}

function SheetClose({ ...props }: ComponentProps<typeof SheetPrimitive.Close>) {
	return <SheetPrimitive.Close data-slot="sheet-close" {...props} />
}

function SheetPortal({
	...props
}: ComponentProps<typeof SheetPrimitive.Portal>) {
	return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />
}

function SheetOverlay({
	className,
	...props
}: ComponentProps<typeof SheetPrimitive.Overlay>) {
	return (
		<SheetPrimitive.Overlay
			data-slot="sheet-overlay"
			className={cn(
				"data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/80",
				className
			)}
			{...props}
		/>
	)
}

function SheetContent({
	className,
	children,
	side = "right",
	showCloseButton = false,
	size = "default",
	...props
}: ComponentProps<typeof SheetPrimitive.Content> & {
	side?: "top" | "right" | "bottom" | "left"
	showCloseButton?: boolean
	size?: "default" | "preview"
}) {
	return (
		<SheetPortal>
			<SheetOverlay />
			<SheetPrimitive.Content
				data-slot="sheet-content"
				className={cn(
					"bg-background-secondary data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col gap-4 surface-floating transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
					side === "right" &&
						"data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right top-[var(--orbit-safe-area-top)] right-[var(--orbit-safe-area-right)] bottom-[var(--orbit-safe-area-bottom)] h-auto w-3/4 border-l sm:max-w-sm",
					side === "left" &&
						"data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left top-[var(--orbit-safe-area-top)] bottom-[var(--orbit-safe-area-bottom)] left-[var(--orbit-safe-area-left)] h-auto w-3/4 border-r sm:max-w-sm",
					side === "top" &&
						"data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top top-[var(--orbit-safe-area-top)] right-[var(--orbit-safe-area-right)] left-[var(--orbit-safe-area-left)] h-auto border-b",
					side === "bottom" &&
						"data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom right-[var(--orbit-safe-area-right)] bottom-[var(--orbit-safe-area-bottom)] left-[var(--orbit-safe-area-left)] h-auto border-t",
					size === "preview" &&
						"w-full gap-0 overflow-hidden p-0 sm:max-w-none md:w-[min(48rem,70vw)]",
					className
				)}
				{...props}
			>
				{showCloseButton && (
					<SheetClose asChild>
						<Button
							variant="ghost"
							size="icon-md"
							className="absolute right-3 top-3"
							aria-label="Close panel"
						>
							<XIcon />
						</Button>
					</SheetClose>
				)}
				{children}
			</SheetPrimitive.Content>
		</SheetPortal>
	)
}

function SheetHeader({ className, ...props }: ComponentProps<"div">) {
	return (
		<div
			data-slot="sheet-header"
			className={cn("flex flex-col gap-1.5 px-4", className)}
			{...props}
		/>
	)
}

function SheetFooter({ className, ...props }: ComponentProps<"div">) {
	return (
		<div
			data-slot="sheet-footer"
			className={cn("mt-auto flex flex-col gap-2 p-4", className)}
			{...props}
		/>
	)
}

function SheetTitle({
	className,
	...props
}: ComponentProps<typeof SheetPrimitive.Title>) {
	return (
		<SheetPrimitive.Title
			data-slot="sheet-title"
			className={cn("text-foreground font-semibold tracking-tight", className)}
			{...props}
		/>
	)
}

function SheetDescription({
	className,
	...props
}: ComponentProps<typeof SheetPrimitive.Description>) {
	return (
		<SheetPrimitive.Description
			data-slot="sheet-description"
			className={cn("text-muted-foreground text-sm", className)}
			{...props}
		/>
	)
}

export {
	Sheet,
	SheetClose,
	SheetContent,
	SheetDescription,
	SheetFooter,
	SheetHeader,
	SheetTitle,
	SheetTrigger
}
